// Kundenpflege-Modus — zwei Wochen lang wird der Kundenstamm am Stop geprüft.
//
// Der Stamm kommt aus zwei gewachsenen Excel-Listen: 58 Kunden ohne Namen, keine
// einzige E-Mail, 48 Adress-Tippfehler. Der Einzige, der das zuverlässig richten
// kann, steht mit dem Kunden vor sich an der Haustür. Also fragt ihn die App
// genau dort — bevor er kassiert, nicht danach: Nach dem Kassieren ist der Kunde
// weg und der Fahrer im nächsten Auto.
//
// Nach Ablauf verschwindet der Modus von selbst. Ein Schalter, den jemand
// ausschalten MUSS, bleibt vergessen an, und was dauernd nervt, wird weggeklickt.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface KundenpflegeStand {
  /** Läuft der Modus heute noch? */
  aktiv: boolean;
  /** Letzter Tag (einschließlich), an dem bestätigt werden muss. */
  bis: string | null;
  offen: number;
  bestaetigt: number;
}

const heute = () => new Date().toISOString().slice(0, 10);

export function useKundenpflege() {
  return useQuery({
    queryKey: ["kundenpflege"],
    // Eine Minute reicht: Schaltet ein Admin den Modus ab, darf der Fahrer das
    // ruhig eine Minute später merken. Häufiger fragen hieße, bei 220 Stops
    // 220-mal dieselbe Antwort zu holen.
    staleTime: 60_000,
    queryFn: async (): Promise<KundenpflegeStand> => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "kundenpflege_bis")
        .maybeSingle();
      if (error) throw error;

      const bis = typeof data?.value === "string" ? data.value : null;
      const aktiv = !!bis && heute() <= bis;

      // Zählen kostet zwei Kopfabfragen ohne Zeilen — das trägt auch der Fahrer
      // im Funkloch. Nur wenn der Modus überhaupt läuft.
      if (!aktiv) return { aktiv, bis, offen: 0, bestaetigt: 0 };

      const [{ count: offen }, { count: bestaetigt }] = await Promise.all([
        supabase
          .from("customers")
          .select("id", { count: "exact", head: true })
          .is("data_confirmed_at", null)
          .eq("is_active", true),
        supabase
          .from("customers")
          .select("id", { count: "exact", head: true })
          .not("data_confirmed_at", "is", null)
          .eq("is_active", true),
      ]);

      return { aktiv, bis, offen: offen ?? 0, bestaetigt: bestaetigt ?? 0 };
    },
  });
}

/**
 * Modus verlängern oder vorzeitig beenden (nur Admin/Büro, per RLS).
 *
 * `null` beendet ihn: Ein leeres Enddatum heißt "läuft nicht mehr". Der Eintrag
 * bleibt stehen, damit man später sieht, dass es den Modus gab.
 */
export function useKundenpflegeSetzen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bis: string | null) => {
      const { error } = await supabase
        .from("app_settings")
        .update({ value: bis })
        .eq("key", "kundenpflege_bis");
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kundenpflege"] }),
  });
}

/** Die Daten, die am Stop geprüft werden — Kontakt und Lieferhinweis. */
export interface KundenpflegeDaten {
  display_name: string;
  phone: string | null;
  email: string | null;
  type: "privat" | "gewerblich";
  street: string;
  zip: string;
  city: string;
  delivery_notes: string | null;
}

/**
 * Kunde + Anschrift in einem Rutsch, so wie das Fenster sie braucht.
 *
 * Getrennte Tabellen (customers / customer_addresses), deshalb zwei Schreibwege.
 * Der Bestätigungsstempel wird ZULETZT gesetzt: Scheitert das Speichern der
 * Adresse, steht der Kunde nicht fälschlich als geprüft da.
 */
export function useKundeBestaetigen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      customerId,
      addressId,
      daten,
    }: {
      customerId: string;
      addressId: string | null;
      daten: KundenpflegeDaten;
    }) => {
      const { error: kErr } = await supabase
        .from("customers")
        .update({
          display_name: daten.display_name.trim() || null,
          phone: daten.phone?.trim() || null,
          email: daten.email?.trim() || null,
          type: daten.type,
        })
        .eq("id", customerId);
      if (kErr) throw kErr;

      if (addressId) {
        const { error: aErr } = await supabase
          .from("customer_addresses")
          .update({
            street: daten.street.trim(),
            zip: daten.zip.trim(),
            city: daten.city.trim(),
            delivery_notes: daten.delivery_notes?.trim() || null,
          })
          .eq("id", addressId);
        if (aErr) throw aErr;
      }

      const { data: ich } = await supabase.auth.getUser();
      const { error: sErr } = await supabase
        .from("customers")
        .update({
          data_confirmed_at: new Date().toISOString(),
          data_confirmed_by: ich.user?.id ?? null,
        })
        .eq("id", customerId);
      if (sErr) throw sErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-tour"] });
      qc.invalidateQueries({ queryKey: ["tour"] });
      qc.invalidateQueries({ queryKey: ["kundenpflege"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}
